import {inject, Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {
  CompanyInfoFieldsInput,
  CompanyInfoResponse,
  CompanyInfoSection,
  GetCompanyInfoRequestParams,
  ProjectCompanyInfoControllerService,
  UpsertSectionRequestParams,
} from '@frontend/shared-api';
import {DirectusSelect, DirectusService} from '@frontend/directus';
import {CompanyInfoValues} from './step-1-1-company-information.types';

export const STEP_1_1_COLLECTIONS = {
  INDUSTRY: 'industry',
  TARIFF: 'tariff',
};

export function toCompanyInfoValues(response: CompanyInfoResponse): CompanyInfoValues {
  return {
    industryId: response.industryId ?? null,
    tariffId: response.tariffId ?? null,
    salariedHeadcountFullTime: response.salariedHeadcountFullTime ?? null,
    salariedHeadcountPartTime: response.salariedHeadcountPartTime ?? null,
    salariedFte: response.salariedFte ?? null,
    industrialHeadcountFullTime: response.industrialHeadcountFullTime ?? null,
    industrialHeadcountPartTime: response.industrialHeadcountPartTime ?? null,
    industrialFte: response.industrialFte ?? null,
    hrHeadcountFullTime: response.hrHeadcountFullTime ?? null,
    hrHeadcountPartTime: response.hrHeadcountPartTime ?? null,
    hrFte: response.hrFte ?? null,
    contingentHeadcountFullTime: response.contingentHeadcountFullTime ?? null,
    contingentHeadcountPartTime: response.contingentHeadcountPartTime ?? null,
    contingentFte: response.contingentFte ?? null,
    pensioners: response.pensioners ?? null,
    inactiveEmployees: response.inactiveEmployees ?? null,
  };
}

export function toCompanyInfoFieldsInput(
  values: Partial<CompanyInfoValues>,
): CompanyInfoFieldsInput {
  return {
    industryId: values.industryId ?? undefined,
    tariffId: values.tariffId ?? undefined,
    salariedHeadcountFullTime: values.salariedHeadcountFullTime ?? undefined,
    salariedHeadcountPartTime: values.salariedHeadcountPartTime ?? undefined,
    salariedFte: values.salariedFte ?? undefined,
    industrialHeadcountFullTime: values.industrialHeadcountFullTime ?? undefined,
    industrialHeadcountPartTime: values.industrialHeadcountPartTime ?? undefined,
    industrialFte: values.industrialFte ?? undefined,
    hrHeadcountFullTime: values.hrHeadcountFullTime ?? undefined,
    hrHeadcountPartTime: values.hrHeadcountPartTime ?? undefined,
    hrFte: values.hrFte ?? undefined,
    contingentHeadcountFullTime: values.contingentHeadcountFullTime ?? undefined,
    contingentHeadcountPartTime: values.contingentHeadcountPartTime ?? undefined,
    contingentFte: values.contingentFte ?? undefined,
    pensioners: values.pensioners ?? undefined,
    inactiveEmployees: values.inactiveEmployees ?? undefined,
  };
}

@Injectable({
  providedIn: 'root',
})
export class Step11CompanyInformationService {
  private readonly projectCompanyInfoService = inject(ProjectCompanyInfoControllerService);
  private readonly directusService = inject(DirectusService);

  loadIndustryDropdown(): Observable<DirectusSelect> {
    return this.directusService.loadDropdownWithValues(STEP_1_1_COLLECTIONS.INDUSTRY);
  }

  loadTariffDropdown(): Observable<DirectusSelect> {
    return this.directusService.loadDropdownWithValues(STEP_1_1_COLLECTIONS.TARIFF);
  }

  loadCompanyInfo(projectId: number, officeId: number): Promise<CompanyInfoResponse> {
    const request: GetCompanyInfoRequestParams = {projectId, officeId};
    return firstValueFrom(this.projectCompanyInfoService.getCompanyInfo(request));
  }

  saveSection(
    projectId: number,
    officeId: number,
    section: CompanyInfoSection,
    values: Partial<CompanyInfoValues>,
  ): Promise<CompanyInfoResponse> {
    const request: UpsertSectionRequestParams = {
      projectId,
      officeId,
      companyInfoSectionRequest: {section, fields: toCompanyInfoFieldsInput(values)},
    };
    return firstValueFrom(this.projectCompanyInfoService.upsertSection(request));
  }
}
